import { z } from 'zod';
/** Every entity id in the CRM is a UUID; the API never exposes serial integers. */
export const idSchema = z.uuid();
/** ISO-8601 instant. The wire format is always UTC; the team's day boundary is Eastern
 *  (`America/New_York`) and is applied where a day is computed, never on the wire. */
export const instantSchema = z.iso.datetime({ offset: true });
/** The columns every persisted entity carries. */
export const baseEntitySchema = z.object({
    id: idSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
});
/** Cursor pagination — a list endpoint takes this and returns `pageSchema(item)`. */
export const pageQuerySchema = z.object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export function pageSchema(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().nullable(),
    });
}
/**
 * The error envelope every non-2xx response carries (RELAY-932):
 *   { error: { code, message, details? }, request_id }
 * `code` is a stable machine string (`not_found`, `validation_error`, …), `message` is for
 * humans, `details` is optional structured context (validation issues, the conflicting
 * constraint), and `request_id` is the id echoed in the `x-request-id` response header —
 * what a user pastes into a support thread and what finds the trace and the log lines.
 *
 * This deliberately differs from Relay's flat `{ error, code }`: decided in RELAY-932.
 */
export const errorBodySchema = z.object({
    error: z.object({
        code: z.string().min(1),
        message: z.string().min(1),
        details: z.unknown().optional(),
    }),
    request_id: z.string().min(1),
});
/** A comparable form: sorted object keys, dates as ISO, so equal data compares equal. */
export function stable(v) {
    const norm = (x) => {
        if (x instanceof Date)
            return x.toISOString();
        if (Array.isArray(x))
            return x.map(norm);
        if (x && typeof x === 'object')
            return Object.fromEntries(Object.keys(x)
                .sort()
                .map((k) => [k, norm(x[k])]));
        return x === undefined ? null : x;
    };
    return JSON.stringify(norm(v));
}
//# sourceMappingURL=common.js.map